import type { HotkeyConfig, HotkeyStorage, UserPreferences } from "../types";
import { createStorage } from "./storage";

const PREFERENCES_KEY = "user-preferences";

const DEFAULT_PREFERENCES: UserPreferences = {
  enabled: true,
  requireModifiers: false,
  customMappings: {},
  showIndicators: true,
  announceToScreenReader: true,
  disableWhenInputFocused: true,
  disabledShortcuts: [],
};

export class PreferencesManager {
  private preferences: UserPreferences;
  private storage: HotkeyStorage;
  private listeners: Set<(preferences: UserPreferences) => void>;

  private cachedPreferences: UserPreferences | null = null;
  private cachedDisabledShortcuts: readonly string[] | null = null;
  private cachedExportJson: string | null = null;
  private disabledShortcutsSet: Set<string> | null = null;
  private preferencesVersion: number = 0;

  constructor(storage?: HotkeyStorage) {
    this.storage = storage || createStorage();
    this.listeners = new Set();
    this.preferences = this.loadPreferences();
  }

  private invalidateCaches(): void {
    this.preferencesVersion++;
    this.cachedPreferences = null;
    this.cachedDisabledShortcuts = null;
    this.cachedExportJson = null;
    this.disabledShortcutsSet = null;
  }

  private loadPreferences(): UserPreferences {
    const stored = this.storage.get<UserPreferences>(PREFERENCES_KEY);

    if (stored) {
      return { ...DEFAULT_PREFERENCES, ...stored };
    }

    return { ...DEFAULT_PREFERENCES };
  }

  private savePreferences(): void {
    this.storage.set(PREFERENCES_KEY, this.preferences);
    this.invalidateCaches();
    this.notifyListeners();
  }

  getPreferences(): Readonly<UserPreferences> {
    if (this.cachedPreferences === null) {
      this.cachedPreferences = { ...this.preferences };
    }
    return this.cachedPreferences;
  }

  isEnabled(): boolean {
    return this.preferences.enabled;
  }

  toggle(enabled: boolean): void {
    this.preferences.enabled = enabled;
    this.savePreferences();
  }

  setEnabled(enabled: boolean): void {
    this.toggle(enabled);
  }

  get(): Readonly<UserPreferences> {
    return this.getPreferences();
  }

  set(preferences: Partial<UserPreferences>): void {
    this.preferences = { ...this.preferences, ...preferences };
    this.savePreferences();
  }

  updatePreferences(preferences: Partial<UserPreferences>): void {
    this.preferences = { ...this.preferences, ...preferences };
    this.savePreferences();
  }

  subscribe(listener: (preferences: UserPreferences) => void): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  private notifyListeners(): void {
    const prefs = this.getPreferences();
    this.listeners.forEach((listener) => listener(prefs));
  }

  remap(hotkeyId: string, config: HotkeyConfig): void {
    this.setCustomMapping(hotkeyId, config);
  }

  setRequireModifiers(required: boolean): void {
    this.preferences.requireModifiers = required;
    this.savePreferences();
  }

  getCustomMapping(hotkeyId: string): HotkeyConfig | undefined {
    return this.preferences.customMappings[hotkeyId];
  }

  setCustomMapping(hotkeyId: string, config: HotkeyConfig): void {
    this.preferences.customMappings[hotkeyId] = config;
    this.savePreferences();
  }

  removeCustomMapping(hotkeyId: string): void {
    delete this.preferences.customMappings[hotkeyId];
    this.savePreferences();
  }

  clearCustomMappings(): void {
    this.preferences.customMappings = {};
    this.savePreferences();
  }

  isShortcutDisabled(hotkeyId: string): boolean {
    if (this.disabledShortcutsSet === null) {
      const disabled = this.preferences.disabledShortcuts ?? [];
      this.disabledShortcutsSet = new Set(disabled);
    }
    return this.disabledShortcutsSet.has(hotkeyId);
  }

  disableShortcut(hotkeyId: string): void {
    if (!this.preferences.disabledShortcuts) {
      this.preferences.disabledShortcuts = [];
    }
    if (!this.preferences.disabledShortcuts.includes(hotkeyId)) {
      this.preferences.disabledShortcuts.push(hotkeyId);
      this.savePreferences();
    }
  }

  enableShortcut(hotkeyId: string): void {
    if (!this.preferences.disabledShortcuts) {
      return;
    }
    const index = this.preferences.disabledShortcuts.indexOf(hotkeyId);
    if (index > -1) {
      this.preferences.disabledShortcuts.splice(index, 1);
      this.savePreferences();
    }
  }

  getDisabledShortcuts(): readonly string[] {
    if (this.cachedDisabledShortcuts === null) {
      this.cachedDisabledShortcuts = Object.freeze([...(this.preferences.disabledShortcuts ?? [])]);
    }
    return this.cachedDisabledShortcuts;
  }

  setShowIndicators(show: boolean): void {
    this.preferences.showIndicators = show;
    this.savePreferences();
  }

  setAnnounceToScreenReader(announce: boolean): void {
    this.preferences.announceToScreenReader = announce;
    this.savePreferences();
  }

  setDisableWhenInputFocused(disable: boolean): void {
    this.preferences.disableWhenInputFocused = disable;
    this.savePreferences();
  }

  reset(): void {
    this.preferences = { ...DEFAULT_PREFERENCES };
    this.savePreferences();
  }

  exportPreferences(): string {
    if (this.cachedExportJson === null) {
      this.cachedExportJson = JSON.stringify(this.preferences, null, 2);
    }
    return this.cachedExportJson;
  }

  importPreferences(json: string): boolean {
    try {
      const imported = JSON.parse(json) as Partial<UserPreferences>;
      this.preferences = { ...DEFAULT_PREFERENCES, ...imported };
      this.savePreferences();
      return true;
    } catch (error) {
      console.error("Failed to import preferences", error);
      return false;
    }
  }

  clearCaches(): void {
    this.invalidateCaches();
  }

  getCacheStats() {
    return {
      preferencesVersion: this.preferencesVersion,
      hasCachedPreferences: this.cachedPreferences !== null,
      hasCachedDisabledShortcuts: this.cachedDisabledShortcuts !== null,
      hasCachedExportJson: this.cachedExportJson !== null,
      hasDisabledShortcutsSet: this.disabledShortcutsSet !== null,
      disabledShortcutsCount: this.disabledShortcutsSet?.size ?? 0,
    };
  }
}

export function clearPreferencesCaches(manager: PreferencesManager): void {
  manager.clearCaches();
}

export function getPreferencesCacheStats(manager: PreferencesManager) {
  return manager.getCacheStats();
}
